import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  filePreviewLink,
  useWorkspaceFileExistence,
} from '@/features/workspace-files';
import type { TaskExecutionSummary } from '@/lib/api/generated/model';
import type { MarkdownFilePaths } from '@/shared/components/Markdown';
import { MarkdownEditor } from '@/shared/components/markdownEditor/MarkdownEditor';
import { findWorkspacePaths } from '@/shared/components/markdownFilePaths';
import { PendingRows } from '@/shared/components/PendingRows';
import { ReadFailure } from '@/shared/components/ReadFailure';
import { MentionTextarea } from '../components/MentionTextarea';
import { serialize, type MentionedPerson } from '../mentions';
import { DRAWER_PROSE_CLASS } from '../prose';
import { isFinishedRun } from './replyState';
import { threadMessageElementId } from './threadLink';
import { ThreadMessageRow } from './ThreadMessageRow';
import { usePostThreadMessage, useRunThread } from './useRunThread';
import { ACCESS_COPY } from '@/features/workspaces/accessCopy';
import { useCanWork } from '@/features/workspaces/canWork';

/** The box's promise (FR-007, SC-002): the agent answers, and changes nothing. */
export const ASK_HINT =
  'Ask the agent about this run. It can read the run, its files and its branch, but changes nothing.';

/** What a run in progress says where the box would be (Q6). */
export const NOT_FINISHED_YET = 'You can ask once the run has finished';

export interface RunConversationProps {
  workspaceId: string;
  taskId: string;
  execution: Pick<TaskExecutionSummary, 'id' | 'status'>;
  /** Opens the task's Comments, for "Added to task context"; absent where there is no drawer. */
  onOpenComments?: () => void;
  /** A message a link landed on (FR-006): scrolled into view once it is loaded. */
  focusMessageId?: string | null;
}

/**
 * A run's Conversation (spec 149): its thread, then a box to ask the agent
 * about it.
 *
 * The comment timeline's pieces, not a second set: the shared markdown editor
 * with the mention box as its text box, bodies rendered as comments are, and a
 * post appended once stored, the box cleared only then. What differs is what a
 * thread is — an exchange with an agent that reads and never writes — so the
 * box says that, and every person's message carries where its answer stands.
 *
 * Kept current by the stream (`run_thread_changed`), not by polling: the reply
 * states move on their own, and the stream says when.
 *
 * Not virtualised, unlike the comment timeline: it lives inside a row of the
 * virtualised Runs rail, and shows the service's pages of 50 with "Show
 * earlier" instead (plan D9).
 */
export function RunConversation({
  workspaceId,
  taskId,
  execution,
  onOpenComments,
  focusMessageId,
}: RunConversationProps) {
  const finished = isFinishedRun(execution);
  const address = { workspaceId, taskId, executionId: execution.id };
  // A run in progress cannot have been asked anything: there is nothing to read.
  const thread = useRunThread(workspaceId, taskId, execution.id, finished);
  const canWork = useCanWork();
  const messages = useMemo(() => thread.messages ?? [], [thread.messages]);
  const mentioned = useMemo(
    () => [...new Set(messages.flatMap((m) => findWorkspacePaths(m.body)))],
    [messages],
  );
  const existence = useWorkspaceFileExistence(workspaceId, mentioned);
  const filePaths: MarkdownFilePaths = {
    exists: existence.exists,
    linkTo: (path) => filePreviewLink(workspaceId, path),
  };

  // Landed once, when the message first appears: a later refresh must not
  // pull the reader back to it.
  const landed = useRef(false);
  const focusPresent =
    focusMessageId != null && messages.some((m) => m.id === focusMessageId);
  useEffect(() => {
    if (!focusPresent || landed.current || !focusMessageId) return;
    landed.current = true;
    const element = document.getElementById(
      threadMessageElementId(focusMessageId),
    );
    // jsdom draws no layout and has no `scrollIntoView`.
    if (element && 'scrollIntoView' in element) {
      element.scrollIntoView({ block: 'center' });
    }
  }, [focusPresent, focusMessageId]);

  return (
    <section
      aria-label="Conversation"
      className="flex flex-col gap-3 pt-2"
      data-testid="run-conversation"
    >
      <h3 className="text-foreground text-sm font-medium">Conversation</h3>

      {!finished ? (
        <p className="text-muted-foreground text-sm">{NOT_FINISHED_YET}</p>
      ) : (
        <>
          {thread.hasEarlier ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="self-center"
              disabled={thread.loadingEarlier}
              onClick={thread.showEarlier}
            >
              {thread.loadingEarlier ? 'Loading…' : 'Show earlier'}
            </Button>
          ) : null}

          {thread.isPending ? (
            <PendingRows
              label="Loading conversation"
              testId="conversation-state"
            />
          ) : null}

          {thread.loadFailed && messages.length === 0 ? (
            <ReadFailure
              message="The conversation could not be loaded."
              onRetry={thread.refetch}
              isFetching={thread.isFetching}
              testId="conversation-state"
            />
          ) : null}

          {messages.length > 0 ? (
            <ol className="flex flex-col gap-3">
              {messages.map((message) => (
                <li key={message.id}>
                  <ThreadMessageRow
                    message={message}
                    address={address}
                    filePaths={filePaths}
                    onOpenComments={onOpenComments}
                    focused={message.id === focusMessageId}
                  />
                </li>
              ))}
            </ol>
          ) : null}

          {/* Read, not written, by someone who is not a member (spec 189). */}
          {canWork ? (
            <Composer
              workspaceId={workspaceId}
              taskId={taskId}
              executionId={execution.id}
              filePaths={filePaths}
            />
          ) : (
            <p className="text-muted-foreground text-sm">
              {ACCESS_COPY.membersOnlyReply}
            </p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * The box at the Conversation's foot, built as the comment composer is (spec
 * 145): mentions by `@`, Write and Preview, cleared only once the message is
 * stored, and locked while it is sent so nothing typed in that window is lost.
 */
function Composer({
  workspaceId,
  taskId,
  executionId,
  filePaths,
}: {
  workspaceId: string;
  taskId: string;
  executionId: string;
  filePaths: MarkdownFilePaths;
}) {
  const [draft, setDraft] = useState('');
  const [chosenPeople, setChosenPeople] = useState<MentionedPerson[]>([]);
  const post = usePostThreadMessage();
  const hintId = useId();
  const errorId = useId();
  const canSend = draft.trim().length > 0 && !post.isPending;

  const send = () => {
    if (!canSend) return;
    post.mutate(
      {
        workspaceId,
        taskId,
        executionId,
        body: serialize(draft.trim(), chosenPeople),
      },
      {
        onSuccess: () => {
          setDraft('');
          setChosenPeople([]);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <MarkdownEditor
        value={draft}
        onValueChange={setDraft}
        preview={serialize(draft.trim(), chosenPeople)}
        previewClassName={DRAWER_PROSE_CLASS}
        filePaths={filePaths}
        renderInput={(input) => (
          <MentionTextarea
            workspaceId={workspaceId}
            {...input}
            onMention={(person) => {
              setChosenPeople((chosen) => [...chosen, person]);
            }}
          />
        )}
        aria-label="Ask about this run"
        placeholder="Ask the agent about this run"
        rows={3}
        aria-invalid={post.isError}
        aria-describedby={post.isError ? `${hintId} ${errorId}` : hintId}
        disabled={post.isPending}
      />
      <p id={hintId} className="text-muted-foreground text-xs">
        {ASK_HINT} Type @ to mention someone.
      </p>
      {post.isError ? (
        <p id={errorId} role="alert" className="text-destructive text-sm">
          {post.error.message}
        </p>
      ) : null}
      <Button
        type="button"
        size="sm"
        className="self-end"
        disabled={!canSend}
        onClick={send}
      >
        {post.isPending ? 'Sending…' : 'Ask'}
      </Button>
    </div>
  );
}
